import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { CalcMethod, EventKey } from '@app/shared/models/contracts/enums/shared-enums';
import { CapitalStructureDto, CaseDto, ProjectDto } from '@app/shared/models/contracts/project-dto';

export const getInstrumentCreationDates = (
  dividendSetId: string | undefined,
  eligibleInstruments: InstrumentDefinitionWithId[],
  project: ProjectDto,
  capitalStructure: CapitalStructureDto
) => {
  const mapInstrumentsToInvestmentDate = () =>
    eligibleInstruments.map((i) => ({
      instrumentId: i.instrumentId,
      creationDate: project.investmentDate,
    }));

  const matchingCase = project.pwermInput.cases.find((c) => c.dividendSetId === dividendSetId);

  const eventSetId = getEventSetId(
    project.details.calcMethod,
    Object.keys(capitalStructure.eventSets),
    matchingCase
  );

  const eventSet = capitalStructure.eventSets[eventSetId];
  if (!eventSet) {
    return mapInstrumentsToInvestmentDate();
  }

  const events = Object.entries(capitalStructure.events)
    .filter(([key, _]) => eventSet.events.includes(key))
    .sort(([_a, eventA], [_b, eventB]) => eventA.eventDate.localeCompare(eventB.eventDate))
    .map(([_, event]) => ({ ...event }));

  const instrumentsCreationDates = eligibleInstruments
    .map((i) => {
      if (Object.keys(capitalStructure.initialValues).includes(i.instrumentId)) {
        return {
          instrumentId: i.instrumentId,
          creationDate: project.investmentDate,
        };
      }

      const firstEvent = events.find((event) =>
        Object.keys(event.movements ?? {}).includes(i.instrumentId)
      );

      if (firstEvent) {
        return {
          instrumentId: i.instrumentId,
          creationDate: firstEvent?.eventDate,
        };
      }
    })
    .filter((i): i is { instrumentId: string; creationDate: string } => i !== undefined);

  return instrumentsCreationDates;
};

export const getEventSetId = (
  calcMethod: keyof typeof CalcMethod,
  eventSetsIds: string[],
  selectedCase: CaseDto | undefined
) => {
  if (calcMethod === CalcMethod.OPM) {
    const opmOnlyEventSetExists = () => eventSetsIds.includes(EventKey.OpmOnly);
    return opmOnlyEventSetExists() ? EventKey.OpmOnly : EventKey.EmptyEventSet;
  }

  return selectedCase?.eventSetId ?? EventKey.EmptyEventSet;
};
